import React, { useState } from 'react';
import { Download, Loader2, Share2 } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@easyflow/ui-frontend/components/ui/dropdown-menu';
import { ARTIFACT_EXPORT_FORMATS, ARTIFACT_EXPORT_FORMAT_LABELS } from '@easyflow/shared';
import type { ArtifactExportFormat } from '@easyflow/shared';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { ShareArtifactDialog } from './ShareArtifactDialog';
import type { WorkspaceUiArtifact } from './types';

// Task #3846 — azioni Scarica/Condividi uniformi per OGNI tipo di artefatto,
// riusate dal pannello artefatti e dalla mini-card in chat (mobile incluso).
interface Props {
  artifact: WorkspaceUiArtifact;
}

function sanitizeBaseName(title: string): string {
  const cleaned = title
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9-_\s]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 80);
  return cleaned || 'artefatto';
}

/** Nome file lato client (il server manda comunque Content-Disposition). */
function downloadFilename(artifact: WorkspaceUiArtifact, format: ArtifactExportFormat): string {
  if (format === 'original') {
    const name = (artifact.content as { name?: unknown } | null)?.name;
    if (typeof name === 'string' && name.trim()) return name.trim();
    return sanitizeBaseName(artifact.title);
  }
  return `${sanitizeBaseName(artifact.title)}.${format}`;
}

export const ArtifactActions: React.FC<Props> = ({ artifact }) => {
  const { toast } = useToast();
  const [downloading, setDownloading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const formats = ARTIFACT_EXPORT_FORMATS[artifact.kind] ?? [];
  if (formats.length === 0) return null;

  const handleDownload = async (format: ArtifactExportFormat) => {
    if (downloading) return;
    setDownloading(true);
    try {
      const blob = await apiClient.getBlob(
        `/personal-assistant/workspace/artifacts/${artifact.id}/export?format=${format}`,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadFilename(artifact, format);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        title: 'Download fallito',
        description: (err as Error).message || "Errore imprevisto durante l'export",
        variant: 'destructive',
      });
    } finally {
      setDownloading(false);
    }
  };

  const downloadIcon = downloading
    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
    : <Download className="h-3.5 w-3.5" />;

  return (
    <div className="flex shrink-0 items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
      {formats.length === 1 ? (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          title="Scarica"
          disabled={downloading}
          onClick={() => void handleDownload(formats[0] as ArtifactExportFormat)}
        >
          {downloadIcon}
        </Button>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Scarica" disabled={downloading}>
              {downloadIcon}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {formats.map(f => (
              <DropdownMenuItem key={f} onClick={() => void handleDownload(f)}>
                {ARTIFACT_EXPORT_FORMAT_LABELS[f]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0"
        title="Condividi via Messenger"
        onClick={() => setShareOpen(true)}
      >
        <Share2 className="h-3.5 w-3.5" />
      </Button>
      {shareOpen && (
        <ShareArtifactDialog artifact={artifact} open={shareOpen} onClose={() => setShareOpen(false)} />
      )}
    </div>
  );
};
